// apps/web/src/features/crm/my-day/components/MyDayPage.tsx
//
// `/my-day` — manager's task queue (#1083). Renders the four buckets the
// backend produces (overdue / today / upcoming / blocked) plus a header
// with totals. RBAC: route is already gated by `tasks.read` in
// `routes.tsx`; the complete affordance is `tasks.manage`-gated here so a
// read-only viewer cannot fire a 403.
//
// The timezone defaults to the backend's `Europe/Moscow` anchor and the
// page echoes whichever timezone the backend resolved. v1 has no tz
// picker — the issue's non-goals exclude an executive analytics surface
// and the manager-side workspace runs on the business clock.
import { useTranslation } from 'react-i18next';
import { MY_DAY_DEFAULT_TIMEZONE, type MyDayBucketKind } from '@magimp/shared';
import { useMyDay } from '@magimp/shared-hooks';
import { EmptyState, ErrorState, LoadingState } from '../../../../ui/index.js';
import { errorMessage } from '../../../../api/errors.js';
import { hasPermission, useAuthStore } from '../../../../stores/auth.js';
import { MyDayBucketSection } from './MyDayBucketSection.js';
import { MyDayRequestSignalsSection } from './MyDayRequestSignalsSection.js';

// WHY: deterministic visual order — overdue first (most urgent), then
// today, upcoming, blocked. Backend response key order is not guaranteed
// across rebuilds, so we project explicitly.
const BUCKET_ORDER: readonly MyDayBucketKind[] = ['overdue', 'today', 'upcoming', 'blocked'];

export function MyDayPage() {
  const { t } = useTranslation(['myDay', 'common']);
  const perms = useAuthStore((s) => s.permissions);
  const canManage = perms === 'ALL' || hasPermission(perms, 'tasks.manage');
  const q = useMyDay({ tz: MY_DAY_DEFAULT_TIMEZONE });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1, minWidth: 0 }}>
          {t('myDay:title')}
        </h1>
        {q.isSuccess && (
          <div style={{ fontSize: 12, color: 'var(--fg-2)' }} data-testid="my-day-tz">
            {t('myDay:timezone', { tz: q.data.timezone })}
          </div>
        )}
      </header>
      {q.isPending && <LoadingState />}
      {q.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(q.error)}
          onRetry={() => void q.refetch()}
        />
      )}
      {q.isSuccess && (
        <>
          {totalItems(q.data.counts) === 0 && (
            <div data-testid="my-day-empty">
              <EmptyState title={t('myDay:empty.title')} text={t('myDay:empty.text')} />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {BUCKET_ORDER.map((kind) => (
              <MyDayBucketSection
                key={kind}
                kind={kind}
                items={q.data.buckets[kind]}
                canManage={canManage}
                timezone={q.data.timezone}
              />
            ))}
          </div>
        </>
      )}
      {/* WHY (#1600 FR-6/AC-6): rendered outside the lead-task `q` branches so
          a request-signal failure and a lead-task failure stay isolated. */}
      <MyDayRequestSignalsSection />
    </div>
  );
}

function totalItems(counts: { overdue: number; today: number; upcoming: number; blocked: number }) {
  return counts.overdue + counts.today + counts.upcoming + counts.blocked;
}
